import {
  PrimaryButton,
  Section,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getCurrentQuote,
  getUsablePaymentMethod,
  PaymentMethod,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  useStripeCheckoutAdmin,
  useStripeCheckoutUser,
} from '../../../../../hooks/stripeCheckout'
import { useStripeSetup } from '../../../../../hooks/stripeSetup'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../proposalContext'
import { CheckoutPath } from '../CheckoutPath'

export interface CheckoutCardProps {
  config: PaymentScheduleConfig
}

const tKey = TKey('components.checkout-card')

export const CheckoutCard: FunctionComponent<CheckoutCardProps> = ({ config }) => {
  const { t } = useWriskTranslation()

  const { apiClient, isAdmin } = usePrincipal()

  const { proposal, setProposal, refresh } = useProposal()
  const { product } = useProduct()
  const { paymentMethods } = usePaymentMethods()

  const quote = getCurrentQuote(proposal)

  const paymentMethod = getUsablePaymentMethod(paymentMethods, PaymentMethodType.CARD)
  const premiums = useQuotePremiums(quote, product, config.type)

  const checkoutType = getCheckoutType(premiums, paymentMethod)

  const onStripeUserCheckout = useStripeCheckoutUser({
    saveCard: true,
    proposalCode: proposal.proposalCode,
    successPath: [location.pathname, CheckoutPath.CHECKOUT_CALLBACK].join('/'),
    cancelPath: location.pathname,
  })

  const onStripeAdminCheckout = useStripeCheckoutAdmin({
    saveCard: true,
    proposalCode: proposal.proposalCode,
    onSuccess: refresh,
  })

  const onSetupStripe = useStripeSetup({
    clientReference: proposal.proposalCode,
    successPath: location.pathname,
    cancelPath: location.pathname,
  })

  const onCheckout = useApiErrorHandlingAsyncCallback(async () => {
    switch (checkoutType) {
      case 'PAY':
        return isAdmin
          ? await onStripeAdminCheckout.execute()
          : await onStripeUserCheckout.execute()
      case 'SETUP':
        return await onSetupStripe.execute()
      case 'BIND':
        return setProposal(
          await apiClient.bindProposal(proposal.proposalCode, {
            paymentMethodType: PaymentMethodType.CARD,
          }),
        )
    }
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('title'))}</SectionTitle>
        <SectionDescription>
          {t([
            tKey('description', config.type, checkoutType),
            tKey('description', config.type),
            tKey('description'),
          ])}
        </SectionDescription>
      </Section>
      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        onClick={onCheckout.execute}
        loading={onCheckout.loading}
      >
        {t([
          tKey('action', config.type, checkoutType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getCheckoutType = (
  premiums: QuotePremiums,
  paymentMethod: PaymentMethod | undefined,
): 'PAY' | 'BIND' | 'SETUP' =>
  premiums.totals.discounted > 0 ? 'PAY' : paymentMethod ? 'BIND' : 'SETUP'
